import { cn } from "@shared/ui";
import { CircleAlert } from "lucide-react";
import { ReactNode, useEffect, useRef } from "react";

export const inputClassName =
  "h-control-lg w-full rounded border border-control bg-surface px-3 text-body-lg text-ink placeholder:text-ink-secondary transition-colors hover:border-ink-secondary focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink aria-[invalid=true]:border-danger-ink";

type InlineErrorProps = {
  id?: string;
  children: ReactNode;
  className?: string;
};

export const InlineError = ({ id, children, className }: InlineErrorProps) => (
  <p
    id={id}
    role="alert"
    className={cn("flex items-start gap-1.5 text-small text-danger-ink", className)}
  >
    <CircleAlert aria-hidden className="size-4 shrink-0" strokeWidth={2} />
    <span>{children}</span>
  </p>
);

type FieldProps = {
  id: string;
  label: ReactNode;
  optional?: boolean;
  error?: ReactNode;
  children: ReactNode;
};

export const Field = ({ id, label, optional, error, children }: FieldProps) => {
  const ref = useRef<HTMLDivElement>(null);

  // Bring a newly shown error into view, e.g. after submitting a long form scrolled past it.
  // scroll-my-6 keeps 24px between the field and the edge it scrolls to.
  useEffect(() => {
    if (!error) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    ref.current?.scrollIntoView?.({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
  }, [error]);

  return (
    <div ref={ref} className="flex scroll-my-6 flex-col gap-1.5">
      <label htmlFor={id} className="text-body-medium text-ink">
        {label}
        {optional ? <span className="font-normal text-ink-secondary"> (optional)</span> : null}
      </label>
      {children}
      {error ? <InlineError id={`${id}-error`}>{error}</InlineError> : null}
    </div>
  );
};
